import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  PartnerReferralsView,
  ReferralDTO,
  ReferralStage,
} from "../../shared/types";
import { useNative } from "../../shared/phone/media";
import { usePartnerAuth } from "../context/PartnerAuthContext";
import { partnerApi, PartnerApiError } from "../lib/api";
import { ReferralsNative, type ReferralPreset, type ReferralRange } from "./ReferralsNative";

/**
 * The partner portal referrals list (node P3): every person the signed-in partner
 * brought in, for the selected program. Real, scoped to the session + program via
 * GET /api/partner/referrals (never a client-supplied partner id). The KPI row
 * (Clicked, Signed up, Became customer) is lifetime; the date-range filter narrows
 * the loaded rows by their real joined date, and honest empty / empty-range states
 * cover no referrals and a filter that matches none.
 */

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: "$",
  CAD: "$",
  AUD: "$",
  EUR: "€",
  GBP: "£",
};

function money(cents: number, currency: string): string {
  const symbol = CURRENCY_SYMBOL[currency] ?? "";
  const dollars = cents / 100;
  const body = Number.isInteger(dollars)
    ? dollars.toLocaleString("en-US")
    : dollars.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${symbol}${body}`;
}

function count(n: number): string {
  return n.toLocaleString("en-US");
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/** The date part ("YYYY-MM-DD") of a sqlite date/datetime as a local Date, or null. */
function joinedDate(s: string): Date | null {
  const day = s.slice(0, 10);
  const d = new Date(`${day}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "2026-08-18" -> "Aug 18". Falls back to the raw string if unparseable. */
function shortDate(s: string): string {
  const d = joinedDate(s);
  if (!d) return s;
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** A person's 2-letter monogram from their label, e.g. "J. Marshall" -> "J·M". */
function initials(label: string | null): string {
  const parts = (label ?? "").split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return last ? `${first.toUpperCase()}·${last.toUpperCase()}` : first.toUpperCase();
}

/** The stage pill class + label, matching the portal prototype. */
function stagePill(stage: ReferralStage): { cls: string; label: string } {
  switch (stage) {
    case "customer":
      return { cls: "pill ok", label: "Customer" };
    case "onhold":
      return { cls: "pill hold", label: "On hold" };
    case "trial":
      return { cls: "pill new", label: "Trial" };
    case "signup":
      return { cls: "pill new", label: "Signed up" };
    case "churned":
      return { cls: "pill off", label: "Churned" };
    case "click":
      return { cls: "pill new", label: "Clicked" };
  }
}

/**
 * The earned-column display, derived only from the real referral fields: money for
 * a paid (cleared) reward, money held for an in-flight one, "reversed" for a
 * clawback, "pending" when nothing is earned yet.
 */
function earnedDisplay(r: ReferralDTO, currency: string): { text: string; cls: string } {
  if (r.reversed) return { text: "reversed", cls: "rf-earned dim" };
  if (r.earnedCents > 0 && r.cleared) return { text: money(r.earnedCents, currency), cls: "rf-earned win" };
  if (r.earnedCents > 0) return { text: money(r.earnedCents, currency), cls: "rf-earned" };
  return { text: "pending", cls: "rf-earned dim" };
}

// --- Date-range filter -------------------------------------------------------

type RangeState = ReferralRange;

const PRESETS: ReferralPreset[] = [
  { kind: "today", label: "Today" },
  { kind: "week", label: "This week" },
  { kind: "7", label: "Last 7 days" },
  { kind: "30", label: "Last 30 days" },
];

const DEFAULT_RANGE: RangeState = { kind: "30", label: "Last 30 days" };

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).getTime();
}
function endOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime();
}
function addDays(d: Date, days: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
}

/** The inclusive [from, to] epoch-ms bounds a range selects, over the local day. */
function bounds(range: RangeState, now: Date): { from: number; to: number } {
  const to = endOfDay(now);
  switch (range.kind) {
    case "today":
      return { from: startOfDay(now), to };
    case "week": {
      const dow = (now.getDay() + 6) % 7; // days since Monday
      return { from: startOfDay(addDays(now, -dow)), to };
    }
    case "7":
      return { from: startOfDay(addDays(now, -6)), to };
    case "30":
      return { from: startOfDay(addDays(now, -29)), to };
    case "custom": {
      const from = new Date(`${range.from}T00:00:00`);
      const toD = new Date(`${range.to}T00:00:00`);
      return { from: startOfDay(from), to: endOfDay(toD) };
    }
  }
}

export function ReferralsView() {
  const { currentMembership } = usePartnerAuth();
  const programId = currentMembership?.programId ?? null;

  const [view, setView] = useState<PartnerReferralsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<RangeState>(DEFAULT_RANGE);
  const [drOpen, setDrOpen] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const drRef = useRef<HTMLDivElement>(null);
  const native = useNative();

  const load = useCallback(async (pid: string) => {
    setError(null);
    try {
      const data = await partnerApi.referrals(pid);
      setView(data);
    } catch (err) {
      setView(null);
      setError(
        err instanceof PartnerApiError
          ? err.message
          : "We could not load your referrals. Try again in a moment.",
      );
    }
  }, []);

  useEffect(() => {
    if (!programId) return;
    void load(programId);
  }, [programId, load]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!drRef.current?.contains(e.target as Node)) setDrOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  const pickPreset = useCallback((preset: ReferralPreset) => {
    setRange({ kind: preset.kind, label: preset.label });
    setDrOpen(false);
  }, []);

  const pickDays = useCallback((a: string, b: string) => {
    // Keep the bounds ordered so a from/to entered either way still filters.
    const [from, to] = a <= b ? [a, b] : [b, a];
    setRange({ kind: "custom", label: `${from} to ${to}`, from, to });
    setDrOpen(false);
  }, []);

  const applyCustom = useCallback(() => {
    if (!customFrom || !customTo) return;
    pickDays(customFrom, customTo);
  }, [customFrom, customTo, pickDays]);

  // Real filter: narrow the loaded rows by their joined date to the selected range.
  const filtered = useMemo(() => {
    if (!view) return [];
    const { from, to } = bounds(range, new Date());
    return view.referrals.filter((r) => {
      const d = joinedDate(r.joinedAt);
      if (!d) return false;
      const t = d.getTime();
      return t >= from && t <= to;
    });
  }, [view, range]);

  if (native) {
    return (
      <ReferralsNative
        view={view}
        error={error}
        onRetry={() => programId && void load(programId)}
        range={range}
        presets={PRESETS}
        rows={filtered}
        onPreset={pickPreset}
        onCustom={pickDays}
      />
    );
  }

  if (error && !view) {
    return (
      <div className="content">
        <div className="es rf-error">
          <div className="es-ic" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="M12 8v5M12 16.5v.5" />
              <circle cx="12" cy="12" r="9" />
            </svg>
          </div>
          <h3>Something went wrong</h3>
          <p>{error}</p>
          <button type="button" className="btn ghost" onClick={() => programId && load(programId)}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!view) {
    return (
      <div className="content">
        <div className="home-loading">
          <div className="ps-dot" aria-hidden="true" />
          <span>Loading your referrals...</span>
        </div>
      </div>
    );
  }

  // No referrals at all: the honest empty state, no KPIs or table.
  if (view.referrals.length === 0) {
    return (
      <div className="content app-view">
        <div className="es">
          <div className="es-ic" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <circle cx="9" cy="8" r="3.2" />
              <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
              <path d="M16 6.5a3 3 0 0 1 0 5.6M17.5 19a5.4 5.4 0 0 0-3-4.8" />
            </svg>
          </div>
          <h3>No referrals yet</h3>
          <p>
            When someone taps your link and signs up, they appear here with their stage and what you
            earned.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="content app-view">
      <div className="grid kpis rf-kpis">
        <div className="kpi">
          <div className="k">Clicked</div>
          <div className="v">{count(view.kpis.clicks)}</div>
        </div>
        <div className="kpi">
          <div className="k">Signed up</div>
          <div className="v">{count(view.kpis.signups)}</div>
        </div>
        <div className="kpi">
          <div className="k">Became customer</div>
          <div className="v">{count(view.kpis.customers)}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-h">
          <h3>People you brought in</h3>
          <div className="daterange" ref={drRef}>
            <button
              type="button"
              className="dr-btn"
              aria-haspopup="true"
              aria-expanded={drOpen}
              onClick={(e) => {
                e.stopPropagation();
                setDrOpen((o) => !o);
              }}
            >
              <span>{range.label}</span>
              <svg className="chev" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7 10l5 5 5-5" />
              </svg>
            </button>
            {drOpen && (
              <div className="dr-menu" onClick={(e) => e.stopPropagation()}>
                {PRESETS.map((p) => (
                  <button
                    key={p.kind}
                    type="button"
                    className={range.kind === p.kind ? "on" : ""}
                    onClick={() => pickPreset(p)}
                  >
                    {p.label}
                  </button>
                ))}
                <div className="dr-custom">
                  <div className="dr-lbl">Custom range</div>
                  <div className="dr-inputs">
                    <input
                      type="date"
                      aria-label="From date"
                      value={customFrom}
                      onChange={(e) => setCustomFrom(e.target.value)}
                    />
                    <span>to</span>
                    <input
                      type="date"
                      aria-label="To date"
                      value={customTo}
                      onChange={(e) => setCustomTo(e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn sm"
                    disabled={!customFrom || !customTo}
                    onClick={applyCustom}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="tbl-wrap">
          <table>
            <thead>
              <tr>
                <th>Person</th>
                <th>Joined</th>
                <th>Stage</th>
                <th className="r">You earned</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={4} className="rf-none">
                    No referrals in this range.
                  </td>
                </tr>
              ) : (
                filtered.map((r) => {
                  const pill = stagePill(r.stage);
                  const earned = earnedDisplay(r, view.currency);
                  return (
                    <tr key={r.id}>
                      <td>
                        <div className="person">
                          <span className="pv" aria-hidden="true">
                            {initials(r.personLabel)}
                          </span>
                          <span className="pn">{r.personLabel ?? "New referral"}</span>
                        </div>
                      </td>
                      <td className="num">{shortDate(r.joinedAt)}</td>
                      <td>
                        <span className={pill.cls}>{pill.label}</span>
                      </td>
                      <td className={`r num ${earned.cls}`}>{earned.text}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
